<template>
    <div v-if="series" class="space-y-4">
        <FlexibleCard :headline-id="identifiersCardId" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus(identifiersCardId)"
                    :edit-button-disabled-explanation="editButtonDisabledMessage(identifiersCardId)"
                    :save-button-disabled-explanation="saveButtonDisabledMessage(identifiersCardId)"
                    @enter-edit-mode="onEnterEditMode(identifiersCardId, identifiersCardLabel)"
                    @leave-edit-mode="onLeaveEditMode"
                    @discard-changes="onDiscardChanges"
                    @save="onSave"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ identifiersCardLabel }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>
            <template #body>
                <ContentFormTransition>
                    <BaseForm v-if="showForm(identifiersCardId)" class="p-5" @submit.prevent>
                        <template #fields>
                            <TextField
                                :value="values.nameShort"
                                :label="$t('name-short')"
                                :error-messages="errors.nameShort"
                                :required="true"
                                @input="setFieldValue('nameShort', ($event.target as HTMLInputElement).value)"
                                @blur="setFieldTouched('nameShort', true)"
                            />
                            <TextField
                                :value="values.nameLong"
                                :label="$t('name-long')"
                                :error-messages="errors.nameLong"
                                :required="true"
                                @input="setFieldValue('nameLong', ($event.target as HTMLInputElement).value)"
                                @blur="setFieldTouched('nameLong', true)"
                            />
                            <TextAreaField
                                :value="values.notes"
                                :label="$t('notes')"
                                :error-messages="errors.notes"
                                :required="false"
                                :max-length="notesMaxLength"
                                @input="setFieldValue('notes', ($event.target as HTMLInputElement).value)"
                                @blur="setFieldTouched('notes', true)"
                            />
                            <TextAreaField
                                :value="values.description ?? ''"
                                :label="$t('description')"
                                :error-messages="errors.description"
                                :required="false"
                                :max-length="descriptionMaxLength"
                                @input="setFieldValue('description', ($event.target as HTMLInputElement).value)"
                                @blur="setFieldTouched('description', true)"
                            />
                        </template>
                    </BaseForm>
                    <div v-else>
                        <CardRowText
                            :label="$t('name-short')"
                            :text="valueToString(series.nameShort)"
                            :has-border-bottom="true"
                            :has-copy-button="true"
                        />
                        <CardRowText
                            :label="$t('name-long')"
                            :text="valueToString(series.nameLong)"
                            :has-border-bottom="true"
                            :has-copy-button="true"
                        />
                        <CardRowText
                            :label="$t('notes')"
                            :text="valueToString(series.notes)"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('description')"
                            :text="valueToString(series.description)"
                            :has-border-bottom="false"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>
        <FlexibleCard :headline-id="kapmanCardId" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus(kapmanCardId)"
                    :edit-button-disabled-explanation="editButtonDisabledMessage(kapmanCardId)"
                    :save-button-disabled-explanation="saveButtonDisabledMessage(kapmanCardId)"
                    @enter-edit-mode="onEnterEditMode(kapmanCardId, kapmanCardLabel)"
                    @leave-edit-mode="onLeaveEditMode"
                    @discard-changes="onDiscardChanges"
                    @save="onSave"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ kapmanCardLabel }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>
            <template #body>
                <ContentFormTransition>
                    <BaseForm v-if="showForm(kapmanCardId)" class="p-5" @submit.prevent>
                        <template #fields>
                            <TextAreaField
                                :value="values.explanation"
                                :label="$t('external-event-series-explanation-field-label')"
                                :error-messages="errors.explanation"
                                :required="false"
                                :max-length="explanationMaxLength"
                                @input="setFieldValue('explanation', ($event.target as HTMLInputElement).value)"
                                @blur="setFieldTouched('explanation', true)"
                            />
                        </template>
                    </BaseForm>
                    <div v-else>
                        <CardRowText
                            :label="$t('external-event-series-explanation-field-label')"
                            :text="valueToString(series.explanation)"
                            :has-border-bottom="true"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>
        <FlexibleCard :headline-id="typeCardId" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus(typeCardId)"
                    :edit-button-disabled-explanation="editButtonDisabledMessage(typeCardId)"
                    :save-button-disabled-explanation="saveButtonDisabledMessage(typeCardId)"
                    @enter-edit-mode="onEnterEditMode(typeCardId, typeCardLabel)"
                    @leave-edit-mode="onLeaveEditMode"
                    @discard-changes="onDiscardChanges"
                    @save="onSave"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ typeCardLabel }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>
            <template #body>
                <ContentFormTransition>
                    <BaseForm v-if="showForm(typeCardId)" class="p-5" @submit.prevent>
                        <template #fields>
                            <ComboboxField
                                :value="values.category"
                                :items="getExternalEventSeriesCategoryOptions()"
                                :label="$t('external-event-category')"
                                :error-messages="errors.category"
                                :required="true"
                                @change="onCategoryChange($event)"
                                @blur="setFieldTouched('category', true)"
                            >
                            </ComboboxField>
                            <RadioButtonGroup
                                :label="$t('external-event-national-radio-label')"
                                name="national-choice"
                                :is-required="true"
                                :options="nationalOptions"
                                :disabled-explanation="nationalFieldDisabledExplanation"
                                @change="onNationalChange($event)"
                                @blur="setFieldTouched('national', true)"
                            />
                            <CheckboxField
                                :value="values.silent"
                                :label="$t('external-event-silent-label')"
                                :required="false"
                                :help-button-text="$t('external-event-silent-help')"
                                :disabled-explanation="
                                    silentFieldDisabled ? $t('external-event-silent-disabled-explanation') : undefined
                                "
                                @change="setFieldValue('silent', ($event.target as HTMLInputElement).checked)"
                                @blur="setFieldTouched('silent', true)"
                            />
                        </template>
                    </BaseForm>
                    <div v-else>
                        <CardRowText
                            :label="$t('external-event-category')"
                            :text="$t('external-event-series-category', { category: series.category })"
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('external-event-national-radio-label')"
                            :text="
                                series.national
                                    ? $t('external-event-national-choice')
                                    : $t('external-event-international-choice')
                            "
                            :has-border-bottom="true"
                        />
                        <CardRowText
                            :label="$t('external-event-silent-label')"
                            :text="series.silent ? $t('yes') : $t('no')"
                            :help-text="$t('external-event-silent-help')"
                            :has-border-bottom="false"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>
        <FlexibleCard :headline-id="sourcesCardId" expandable-status="Not-Collapsible">
            <template #header="{ headlineId }">
                <CardHeaderEditable
                    :status="getCardStatus(sourcesCardId)"
                    :edit-button-disabled-explanation="editButtonDisabledMessage(sourcesCardId)"
                    :save-button-disabled-explanation="saveButtonDisabledMessage(sourcesCardId)"
                    @enter-edit-mode="onEnterEditMode(sourcesCardId, sourcesCardLabel)"
                    @leave-edit-mode="onLeaveEditMode"
                    @discard-changes="onDiscardChanges"
                    @save="onSave"
                >
                    <CardHeadline :headline-id="headlineId">
                        {{ sourcesCardLabel }}
                    </CardHeadline>
                </CardHeaderEditable>
            </template>
            <template #body>
                <ContentFormTransition>
                    <BaseForm v-if="showForm(sourcesCardId)" class="p-5" @submit.prevent>
                        <template #fields>
                            <MultiLink
                                :label="$t('external-event-series-websites')"
                                :links="values.websites"
                                :error-messages="errors.websites"
                                @input="setFieldValue('websites', $event)"
                                @delete="setFieldValue('websites', $event)"
                            />
                        </template>
                    </BaseForm>
                    <div v-else>
                        <CardRowAttachmentGroup
                            v-if="websites.length > 0"
                            :label="$t('external-event-series-websites')"
                            :items="websites"
                            :has-border-bottom="false"
                        />
                        <CardRowText
                            v-else
                            :label="$t('external-event-series-websites')"
                            text="-"
                            :has-border-bottom="false"
                        />
                    </div>
                </ContentFormTransition>
            </template>
        </FlexibleCard>
    </div>
</template>
<script setup lang="ts">
import CardHeaderEditable from '@/components/Card/CardHeaderEditable.vue';
import CardHeadline from '@/components/Card/CardHeadline.vue';
import CardRowAttachmentGroup from '@/components/Card/CardRowAttachmentGroup.vue';
import CardRowText from '@/components/Card/CardRowText.vue';
import FlexibleCard from '@/components/Card/FlexibleCard.vue';
import BaseForm from '@/components/Form/BaseForm.vue';
import CheckboxField from '@/components/Form/CheckboxField.vue';
import ComboboxField, { ComboboxItem } from '@/components/Form/ComboboxField.vue';
import RadioButtonGroup from '@/components/Form/RadioButtonGroup.vue';
import TextAreaField from '@/components/Form/TextAreaField.vue';
import TextField from '@/components/Form/TextField.vue';
import { useMultiLink } from '@/components/MultiLink/composables/useMultiLink';
import { toCardRowAttachmentLink } from '@/components/MultiLink/multilink';
import MultiLink from '@/components/MultiLink/MultiLink.vue';
import ContentFormTransition from '@/event/components/ContentFormTransition.vue';
import { useExternalEventSeriesInjection } from '@/external_event/composables/useExternalEventSeriesInjection';
import { getExternalEventSeriesCategoryOptions } from '@/external_event/externalEvent';
import { getExternalEventSeriesUpdateSchema } from '@/external_event/schemas';
import { useActiveCard } from '@/forms/composables/useActiveCard';
import { useForm } from '@/forms/composables/useForm';
import { valueToString } from '@/forms/forms';
import { ExternalEventSeriesCategory } from '@/generated/graphql';
import { useHeadSafe } from '@unhead/vue';
import { useFluent } from 'fluent-vue';
import { computed } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';

const fluent = useFluent();

const { series, externalEventSeriesUpdate, createExternalEventSeriesMultiLink } = useExternalEventSeriesInjection();
const websites = computed(() => toCardRowAttachmentLink(series.value?.websites.links.nodes ?? []));

const {
    activeCard,
    editButtonDisabledMessage,
    saveButtonDisabledMessage,
    onEnterEditMode,
    showForm,
    getCardStatus,
    resetActiveCard,
} = useActiveCard();

const { getFormFieldLinks, saveMultiLinkFields } = useMultiLink(createExternalEventSeriesMultiLink);

const identifiersCardId = 'external-event-series-masterdata';
const identifiersCardLabel = fluent.$t('identifiers');

const kapmanCardId = 'external-event-series-kapman';
const kapmanCardLabel = fluent.$t('kapman-card-label');

const typeCardId = 'external-event-series-type';
const typeCardLabel = fluent.$t('external-event-type');

const sourcesCardId = 'external-event-series-sources';
const sourcesCardLabel = fluent.$t('external-event-sources');

const ExternalEventSeriesUpdateSchema = getExternalEventSeriesUpdateSchema(series.value?.id ?? '');
const descriptionMaxLength = ExternalEventSeriesUpdateSchema.shape.description.maxLength ?? undefined;
const explanationMaxLength = ExternalEventSeriesUpdateSchema.shape.explanation.maxLength ?? undefined;
const notesMaxLength = ExternalEventSeriesUpdateSchema.shape.notes.maxLength ?? undefined;

const websitesId = computed(() => series.value?.websites.id);
const websitesLinks = computed(() => series.value?.websites?.links.nodes ?? []);

const { values, errors, setFieldTouched, setFieldValue, validate, discardForm, submit } = useForm(
    ExternalEventSeriesUpdateSchema,
    computed(() => ({
        nameShort: series.value?.nameShort ?? '',
        nameLong: series.value?.nameLong ?? '',
        notes: series.value?.notes ?? null,
        description: series.value?.description ?? '',
        explanation: series.value?.explanation ?? '',
        category: {
            id: series.value?.category,
            name: fluent.$t('external-event-series-category', { category: series.value?.category ?? '' }),
        },
        national: series.value?.national ?? true,
        silent: series.value?.silent ?? false,
        websites: getFormFieldLinks(websitesLinks),
    })),
);

function onLeaveEditMode() {
    discardForm();
    resetActiveCard();
}

function onDiscardChanges() {
    discardForm();
    resetActiveCard();
}

const nationalOptions = computed(() => {
    return [
        {
            id: 'national',
            label: fluent.$t('external-event-national-choice'),
            isChecked: values.value.national,
            value: 'national',
        },
        {
            id: 'international',
            label: fluent.$t('external-event-international-choice'),
            isChecked: !values.value.national,
            value: 'international',
        },
    ];
});

async function onSave() {
    await submit(async () => {
        const valid = await validate();
        if (valid && series.value) {
            const { websites, ...data } = values.value;
            const mappedData = { ...data, category: data.category.id as ExternalEventSeriesCategory };
            await externalEventSeriesUpdate({ id: series.value?.id, ...mappedData });
            await saveMultiLinkFields([{ id: websitesId.value, fieldName: 'websites', links: values.value.websites }]);
            resetActiveCard();
            discardForm();
        }
    });
}

const hasAllocations = computed(() => {
    if (series.value) {
        if (series.value.events.totalCount === 0) {
            return false;
        }
        return series.value.events.nodes.some((event) =>
            event.externalEventVariantsByEventId.nodes.some(
                (variant) => variant.externalEventAllocationsByVariantId.totalCount > 0,
            ),
        );
    }

    return false;
});

const nationalFieldDisabledExplanation = computed(() => {
    if (hasAllocations.value) {
        return fluent.$t('external-event-event-has-allocations');
    }
});

const silentFieldDisabled = computed(
    () => !(values.value.national === true && values.value.category && values.value.category.id === 'public_holiday'),
);

function onCategoryChange(category: ComboboxItem | null) {
    setFieldValue('category', category);
    if (category && category.id !== 'public_holiday') {
        setFieldValue('silent', false);
    }
}

function onNationalChange(event: Event) {
    const target = event.target as HTMLInputElement;
    setFieldValue('national', target.value === 'national' ? true : false);
    if (target.value !== 'national') {
        setFieldValue('silent', false);
    }
}

onBeforeRouteLeave(() => {
    if (activeCard.value.id && activeCard.value.status === 'Edit-Touched') {
        if (!confirm(fluent.$t('leave-page-warning'))) {
            return false;
        }
        discardForm();
    }
    resetActiveCard();
    return true;
});

useHeadSafe({
    title: () => `${fluent.$t('master-data')} - ${series.value?.nameShort ?? ''} - ${fluent.$t('external-events')}`,
});
</script>
